import { MediaStatus, useCreateMutation } from '../../graphql';
import { useGrippContext } from '../../grippContext';
import { useConfig } from '../../hooks';
import { getFileDataFromUrl, getMediaTypeByExt } from '../mediaUtils';
import { MediaItem, MediaUploadArgs } from '../types';

type BaseHookArgs = {
  args: MediaUploadArgs;
  upload: (
    mediaItem: MediaItem,
    file?: File,
    fileUrl?: string,
    callbacks?: MediaUploadArgs
  ) => Promise<void>;
};

export type UploadMediaOptions = {
  workspaceId?: string;
  file?: File;
  name?: string;
  categories?: Array<{ id: string }>;
  overrideExtension?: string;
  /** Defaults to the legacy early media ID; native composers await transfer. */
  waitForUpload?: boolean;
  /** Captured for this operation, including overlapping uploads. */
  callbacks?: MediaUploadArgs;
};

export const useMediaUpload = (args: BaseHookArgs) => {
  const { data } = useConfig();
  const { logger } = useGrippContext();

  const { mutateAsync } = useCreateMutation({
    query: CREATE_MEDIA,
    modelName: 'media',
  });

  const uploadMedia = async (
    fileUrl: string,
    options: UploadMediaOptions = {}
  ): Promise<string | undefined> => {
    if (!data || !fileUrl) return;

    const { workspaceId, file, name, categories, overrideExtension } = options;

    const mediaItem = createMediaItem(fileUrl, overrideExtension);
    if (name) mediaItem.name = name;

    const input = createMediaInput(mediaItem, { workspaceId, categories });

    const created = await mutateAsync({ input });
    if (!created?.id) throw new Error('Media creation returned no ID');
    mediaItem.id = created.id;
    const callbacks = options.callbacks ?? args.args;
    const transfer = async () => {
      try {
        await args.upload(mediaItem, file, fileUrl, callbacks);
      } catch (error) {
        callbacks.onError?.(error, mediaItem.id);
        throw error;
      }
    };
    if (options.waitForUpload) {
      await transfer();
    } else {
      // Existing callers receive the ID before blob transfer finishes.
      transfer().catch((error: unknown) => {
        logger?.error(error, `Failed to upload media item: ${mediaItem.id}`);
      });
    }
    return created.id;
  };

  return uploadMedia;
};

/** The `createMedia` input for a file about to be uploaded; the offline outbox builds the same one with a client id. */
export const createMediaInput = (
  mediaItem: Pick<MediaItem, 'name' | 'type' | 'extension'>,
  {
    workspaceId,
    categories,
  }: Pick<UploadMediaOptions, 'workspaceId' | 'categories'> = {}
) => ({
  name: mediaItem.name,
  status: MediaStatus.Pending,
  type: mediaItem.type,
  extension: mediaItem.extension,
  ...(workspaceId ? { workspaces: [{ id: workspaceId }] } : {}),
  ...(categories ? { categories } : {}),
});

const createMediaItem = (
  fileUrl: string,
  overrideExtension?: string
): MediaItem => {
  const fileData = getFileDataFromUrl(fileUrl);
  const ext = overrideExtension ?? fileData.ext;
  const name = overrideExtension
    ? swapExtension(fileData.name, overrideExtension)
    : fileData.name;
  return {
    id: '',
    name,
    url: fileUrl,
    extension: ext,
    type: getMediaTypeByExt(ext),
    status: MediaStatus.Pending,
  };
};

export const swapExtension = (fileName: string, newExt: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return `${fileName}.${newExt}`;
  return `${fileName.slice(0, lastDot)}.${newExt}`;
};

export const CREATE_MEDIA = `
  mutation Mutation($input: CreateMediaInput!) {
    createMedia(input: $input) {
      id
    }
  }
`;
